// Shared rich-text sanitization. The client sanitizes editor output before it
// is stored (defense in depth); the server's `sanitize` in
// dynamic_form_helper.rb stays the authoritative second layer. Keep this
// allowlist in sync with `config.action_view.sanitized_allowed_*` in
// rx/config/application.rb so client and server agree on what survives.

import DOMPurify from 'dompurify'

/** Tags the server permits — mirrors `sanitized_allowed_tags`. */
export const RICH_TEXT_ALLOWED_TAGS = [
  'label', 'strong', 'em', 'b', 'i', 'p', 'code', 'pre', 'tt', 'samp', 'kbd',
  'var', 'sub', 'sup', 'dfn', 'cite', 'big', 'small', 'address', 'hr', 'br',
  'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'dl',
  'dt', 'dd', 'abbr', 'acronym', 'a', 'img', 'blockquote', 'del', 'ins', 's',
  'u', 'section', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
]

/** Attributes the server permits — mirrors `sanitized_allowed_attributes`. */
export const RICH_TEXT_ALLOWED_ATTR = [
  'href', 'src', 'width', 'height', 'alt', 'cite', 'datetime', 'title', 'class',
  'name', 'xml:lang', 'abbr', 'id', 'style', 'colspan',
]

/**
 * Sanitize an HTML string against the shared allowlist. Adapters must pass
 * their editor output through this before emitting it via `onChange`, and
 * read-only views run it before injecting stored HTML.
 */
export function sanitizeRichText(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
  })
}

// --- Fields that hold either rich text or plain text ------------------------
// The legacy request form stored rich text (TinyMCE); the new flow and the chat
// tools write plain text. A field that can hold either goes through these at
// the edges: into an editor, and out to anything that wants words.

const HTML_TAG = /<\/?[a-z][^>]*>/i

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * A field's value as editor markup. HTML is sanitized; plain text becomes
 * paragraphs on blank lines and `<br>` on single newlines, since an editor
 * would otherwise run its lines together.
 */
export function toEditorHtml(value: string): string {
  if (!value.trim()) return ''
  if (HTML_TAG.test(value)) return sanitizeRichText(value)

  return value
    .replace(/\r\n?/g, '\n')
    .trim()
    .split(/\n\s*\n/)
    .map((paragraph) => `<p>${escapeHtml(paragraph.trim()).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

const BLOCK_TAGS = 'p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre, tr'

/**
 * A field's value as plain text, one line per block or `<br>`. Plain text is
 * returned trimmed but otherwise as written. Empty for an emptied editor
 * (`<p><br></p>`).
 */
export function richTextToText(value: string): string {
  if (!HTML_TAG.test(value)) return value.trim()

  const el = document.createElement('div')
  el.innerHTML = sanitizeRichText(value)
  el.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  el.querySelectorAll(BLOCK_TAGS).forEach((block) => block.append('\n'))
  // `\s` covers the no-break spaces an editor leaves (`&nbsp;`).
  return (el.textContent ?? '')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
}

// --- Author-supplied captions (label / description / help_text) -------------
// Tighter than the rich-text allowlist above: a caption lives inside `<label>`
// (or `<legend>` / `<th>`), which takes phrasing content only, plus the heading
// tags legacy titles use as a section caption. Everything else degrades to its
// text — DOMPurify keeps element content when it strips a tag.

/**
 * Phrasing content, plus h1–h6. Anything else is unwrapped to its text.
 *
 * The headings are a deliberate exception to the phrasing-only rule: legacy
 * Alpaca titles use them to head a section, and their text *is* the caption, so
 * the control's accessible name stays correct. The cost is that a heading is
 * flow content — it renders as a block and the whole block becomes a click
 * target for the input. That is why `<table>` and friends stay out: there the
 * text is tabular data, not a caption, and it corrupts the accessible name.
 */
export const LABEL_ALLOWED_TAGS = [
  'b', 'strong', 'i', 'em', 'u', 's', 'sub', 'sup', 'small',
  'abbr', 'span', 'a', 'br', 'code', 'wbr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
]

export const LABEL_ALLOWED_ATTR = ['href', 'title', 'class', 'id', 'abbr']

/** Sanitize a caption for injection via `dangerouslySetInnerHTML`. */
export function sanitizeLabelHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: LABEL_ALLOWED_TAGS,
    ALLOWED_ATTR: LABEL_ALLOWED_ATTR,
  })
}

/**
 * Caption reduced to plain text, for aria-labels, button text, and message
 * strings. Reads `textContent` rather than returning DOMPurify's output
 * directly: sanitizing with no allowed tags yields escaped entities
 * (`&lt;18`), which would be read aloud literally.
 */
export function labelToText(html: string): string {
  const el = document.createElement('div')
  el.innerHTML = sanitizeLabelHtml(html)
  el.querySelectorAll('br').forEach((br) => br.replaceWith(' '))
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim()
}
